import { useState, useCallback } from "react";
import addDays from "date-fns/addDays";
import format from "date-fns/format";
import { useQueryClient } from "@tanstack/react-query";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { CalendarConflict } from "./ConflictAlert";
import { CutoffSelectValue } from "./useCalendarEditState";
import { buildCalendarEdits } from "./calendarUtils";

type Params = {
  centerId: string;
  weekStart: Date;
  weekEnd: Date;
  editableRadiologistIds: string[];
  dates: string[];
  getValue: (userId: string, date: string) => CutoffSelectValue;
  isRadiologistOnly: boolean;
  onSaveSuccess: () => void;
};

const useSaveCalendar = ({
  centerId,
  weekStart,
  weekEnd,
  editableRadiologistIds,
  dates,
  getValue,
  isRadiologistOnly,
  onSaveSuccess,
}: Params) => {
  const editRadiologistsCalendarDays = useFirebaseAppFunction(
    "editRadiologistsCalendarDays",
  );
  const queryClient = useQueryClient();

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingConflicts, setPendingConflicts] = useState<CalendarConflict[]>(
    [],
  );
  const [conflictAlertOpen, setConflictAlertOpen] = useState(false);

  const callSave = useCallback(
    async (dryRun: boolean) => {
      const edits = buildCalendarEdits(editableRadiologistIds, dates, getValue);
      const result = await editRadiologistsCalendarDays({
        centerId,
        weekStart: format(weekStart, "yyyy-MM-dd"),
        weekEnd: format(addDays(weekEnd, -1), "yyyy-MM-dd"),
        edits,
        dryRun,
      });
      return result.data?.conflicts ?? [];
    },
    [
      editRadiologistsCalendarDays,
      centerId,
      weekStart,
      weekEnd,
      editableRadiologistIds,
      dates,
      getValue,
    ],
  );

  const commitSave = useCallback(async () => {
    await callSave(false);
    await queryClient.invalidateQueries({ queryKey: ["radiologist-cutoffs"] });
    onSaveSuccess();
  }, [callSave, queryClient, onSaveSuccess]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      if (!isRadiologistOnly) {
        const conflicts = await callSave(true);
        if (conflicts.length > 0) {
          setPendingConflicts(conflicts);
          setConflictAlertOpen(true);
          return;
        }
      }
      await commitSave();
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to save calendar",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleConflictConfirm = async () => {
    setIsSaving(true);
    setSaveError(null);
    setConflictAlertOpen(false);
    try {
      await commitSave();
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to save calendar",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleConflictEdit = () => {
    setConflictAlertOpen(false);
  };

  return {
    isSaving,
    saveError,
    clearSaveError: () => setSaveError(null),
    pendingConflicts,
    conflictAlertOpen,
    handleSave,
    handleConflictConfirm,
    handleConflictEdit,
  };
};

export default useSaveCalendar;
